/**
 * Rate limits, file size limits, and system constants
 */

/** LinkedIn safety limits (applies to all plans) */
export const LINKEDIN_LIMITS = {
  /** Absolute max connections per day (LinkedIn enforced) */
  maxConnectionsPerDay: 100,
  /** Absolute max messages per day (LinkedIn enforced) */
  maxMessagesPerDay: 150,
  /**
   * Connection-note max length. Retained for back-compat with anything that
   * still imports the constant, but DCA no longer attaches notes to invites
   * (Eli / KNWN4 directive — plain invites only). The send path in n8n
   * drops any note before posting to Unipile's /users/invite endpoint.
   */
  connectionNoteMaxLength: 300,
  /** DM max length */
  dmMaxLength: 500,
  /** LinkedIn post max length */
  postMaxLength: 3000,
  /** Max hashtags per post (warning threshold) */
  maxHashtagsPerPost: 5,
} as const

/** Stochastic timing parameters (D36) */
export const TIMING = {
  /** Mean delay between actions in seconds */
  meanDelaySeconds: 120,
  /** Standard deviation for delay in seconds */
  stdDevSeconds: 45,
  /** Minimum delay between actions in seconds */
  minDelaySeconds: 15,
  /** Default activity window start hour */
  defaultWindowStart: 8,
  /** Default activity window end hour */
  defaultWindowEnd: 20,
  /** New account ramp-up: capacity percentage per day [day1, day2, day3, day4] */
  rampUpSchedule: [25, 50, 75, 100] as readonly number[],
} as const

/** File upload limits */
export const FILE_LIMITS = {
  /** Max file size in bytes (10MB) */
  maxFileSizeBytes: 10 * 1024 * 1024,
  /** Max total KB storage for Pro in bytes (50MB) */
  maxKbStoragePro: 50 * 1024 * 1024,
  /** Max total KB storage for Agency in bytes (200MB) */
  maxKbStorageAgency: 200 * 1024 * 1024,
  /** Allowed file extensions */
  allowedExtensions: ['.pdf', '.docx', '.txt'] as readonly string[],
} as const

/** API rate limits (requests per window) */
export const API_RATE_LIMITS = {
  /** General API rate limit per minute */
  generalPerMinute: 60,
  /** AI endpoint rate limit per minute */
  aiPerMinute: 20,
  /** Search endpoint rate limit per minute */
  searchPerMinute: 10,
  /** Webhook endpoint rate limit per minute */
  webhookPerMinute: 100,
} as const

/** Pagination defaults */
export const PAGINATION = {
  defaultPage: 1,
  defaultLimit: 25,
  maxLimit: 100,
} as const

/** Conversation escalation triggers (D20) */
export const ESCALATION = {
  /** Max conversation exchanges before escalation */
  maxExchanges: 5,
} as const
